/**
 * useSCDataPrefetch — Background data prefetch for SC V4 cross-tab validation.
 *
 * Fires on SC_ID mount. Fetches ALL validation-relevant data in parallel
 * (non-blocking — the UI renders the current tab immediately).
 *
 * Cache strategy (3-tier):
 *   L1: Zustand (in-memory, instant, reset on session end)
 *   L2: localStorage via scCacheService (30-60 min TTL, survives page refresh)
 *   L3: API (only on L1+L2 miss or TTL expiry)
 *
 * On subsequent page loads within TTL, 0 API calls are needed — all data
 * is served from localStorage in ~1ms.
 */

import { useEffect } from 'react';
import { useApi } from '../../../../../shared/hooks/useApi';
import { API_ROUTES } from '../../../../../shared/app-configs/sc-app-config';
import { useScStore } from '../../../../../store/scStandard.store';
import { scCacheService } from '../utils/scCacheService';
import { isStandardWithdrawn } from '../utils/scValidation';

export const useSCDataPrefetch = (SC_ID: string | undefined): void => {
  const { executeGet } = useApi();
  const facilityCacheTick = useScStore((state) => state.facilityCacheTick);

  useEffect(() => {
    if (!SC_ID) return; // new SC — nothing to prefetch

    const prefetch = async () => {
      const store = useScStore.getState();

      await Promise.all([
        // ── 1. SC Standards ──────────────────────────────────────────────
        (async () => {
          // L1 hit
          if (store.scStandards.length > 0) return;
          // L2 hit
          const cached = scCacheService.getStandards(SC_ID);
          if (cached) {
            store.setScStandards(cached);
            return;
          }
          // L3: API fetch
          try {
            const res: any = await executeGet(API_ROUTES.GET_SC_DETAILS_V4, { scId: SC_ID });
            if (res?.data) {
              store.setScData(res.data);
            }
            const rawStandards = res?.data?.scStandardInformation;
            const standards: string[] = Array.isArray(rawStandards)
              ? rawStandards
                  .filter((item: any) => !isStandardWithdrawn(item))
                  .map((item: any) => item.scStandard)
                  .filter(Boolean)
              : [];
            if (standards.length > 0) {
              store.setScStandards(standards);
              scCacheService.setStandards(SC_ID, standards);
            }
          } catch (e) {
            console.warn('[useSCDataPrefetch] standards fetch failed:', e);
          }
        })(),

        // ── 2. Facility list → then per-facility details ─────────────────
        (async () => {
          let facilities: any[] = [];

          // L1 hit for list
          if (store.facilityListCache && store.facilityListCache.length > 0) {
            facilities = store.facilityListCache;
          } else {
            // L2 hit for list
            const cachedList = scCacheService.getFacilityList(SC_ID);
            if (cachedList && cachedList.length > 0) {
              facilities = cachedList;
              store.setFacilityListCache(facilities);
            } else {
              // L3: fetch facility list
              try {
                const res: any = await executeGet(API_ROUTES.GET_FACILITY_LIST_DETAILS_V4, { scId: SC_ID });
                // The list endpoint can return data in different shapes — handle both
                const raw = res?.data?.response ?? res?.data ?? [];
                facilities = Array.isArray(raw) ? raw : (raw?.facilityDetails ?? []);
                if (facilities.length > 0) {
                  store.setFacilityListCache(facilities);
                  scCacheService.setFacilityList(SC_ID, facilities);
                }
              } catch (e) {
                console.warn('[useSCDataPrefetch] facility list fetch failed:', e);
                return;
              }
            }
          }

          // For each facility, hydrate Zustand from L2 if available.
          // Note: Background API prefetch of details is disabled per user request.
          // Details will be fetched on-demand when the Facility tab is visited.
          for (const f of facilities) {
            const fid: string | undefined =
              f?.facilityInfo?.facilityId ?? f?.facilityInfo?.facilityTeId ?? f?.facilityId;
            if (!fid) continue;

            // L1 hit
            if (store.facilityDetailsCache[fid]) continue;

            // L2 hit (if it was previously saved/visited)
            const cached = scCacheService.getFacilityDetail(SC_ID, fid);
            if (cached) {
              store.setCachedFacilityDetail(fid, cached);
              continue;
            }
          }
        })(),

        // ── 3. Product Claims (Tab 7) ────────────────────────────────────
        (async () => {
          // L1 hit
          if (store.savedClaims.length > 0) return;
          // L2 hit
          const cached = scCacheService.getClaims(SC_ID);
          if (cached) {
            store.setSavedClaims(cached);
            return;
          }
          // L3: API fetch
          try {
            const res: any = await executeGet(API_ROUTES.GET_PRODUCT_CLAIMS_DETAILS_V4, { scId: SC_ID });
            const claims: string[] = res?.data?.scProductClaimsCategory ?? [];
            const cleanedClaims = claims.map(c => 
              c.replace(/&lt;/g, "<")
               .replace(/&gt;/g, ">")
               .replace(/&amp;/g, "&")
            );
            store.setSavedClaims(cleanedClaims);
            scCacheService.setClaims(SC_ID, cleanedClaims);
          } catch (e) {
            console.warn('[useSCDataPrefetch] claims fetch failed:', e);
          }
        })(),
      ]);
    };

    // Fire and forget — never blocks the UI thread
    prefetch();
  }, [SC_ID, facilityCacheTick]);
};
